import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGame } from "@/context/GameContext";
import { WorldMap } from "@/components/game/WorldMap";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Globe2, X, Coins, Flag, Home, Swords } from "lucide-react";

/**
 * Map section — interactive world of Territoria.
 *
 * • Top: interactive world map of every playable nation (SVG, zoom/pan,
 *   hover tooltips, search, click-to-focus, red-X markers ready for
 *   Phase-4 capital/campaign pins).
 * • Right rail: country/region detail sheet that syncs bidirectionally
 *   with the map — click a country for its region roster, click a region
 *   on the map or a card in the panel to highlight both.
 */
export function MapPanel() {
  const { mapOwnership, setMapOwnership, snap } = useGame();
  const [searchParams] = useSearchParams();
  const currentUserId = snap?.user_id || snap?.owner_id || null;
  const homeIso = snap?.home_country_iso || null;
  const myHolderIso = homeIso;
  const focusIso = searchParams.get("iso") || null;
  const [selected, setSelected] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [nationCount, setNationCount] = useState(null);
  const [regionTotal, setRegionTotal] = useState(null);
  const [claimBusy, setClaimBusy] = useState(false);
  const [countriesReady, setCountriesReady] = useState(false);
  const [autoFocused, setAutoFocused] = useState(false);
  const [presenceMarkers, setPresenceMarkers] = useState([]);

  // Live presence — my dot + friend dots. Refresh every 20s so friends
  // hopping between countries reflect quickly without hammering the API.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/api/game/map/presence");
        if (cancelled) return;
        const pins = [];
        if (data.me) pins.push({ ...data.me, kind: "self", label: `You · ${data.me.iso_a3}` });
        for (const f of data.friends || []) {
          pins.push({ ...f, kind: "friend", label: f.name });
        }
        setPresenceMarkers(pins);
      } catch { /* silent — presence is decorative */ }
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  async function claim(regionId) {
    if (claimBusy) return;
    setClaimBusy(true);
    try {
      const { data } = await api.post("/api/game/map/claim", { region_id: regionId });
      toast.success(`Region claimed for ${data.cost}g.`);
      setMapOwnership?.((prev) => ({ ...prev, [regionId]: data.owner }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClaimBusy(false);
    }
  }
  async function contest(regionId) {
    // Held regions can only change hands through Declare War. This is kept
    // as a stub so the surrounding UI branches compile, but it should never
    // be called from the panel anymore.
    toast.error("Held regions can only change hands through Declare War.");
  }
  async function declareWar(regionId) {
    if (claimBusy) return;
    setClaimBusy(true);
    try {
      const { data } = await api.post("/api/game/country/war/declare", { region_id: regionId });
      toast.success(`War declared on ${data.war.region_name}`, {
        description: `Battle window: ${data.war?.ends_at ? new Date(data.war.ends_at).toLocaleTimeString() : "30 min"} · Rally from the Country panel.`,
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setClaimBusy(false); }
  }
  async function abandon(regionId) {
    if (claimBusy) return;
    setClaimBusy(true);
    try {
      await api.post("/api/game/map/abandon", { region_id: regionId });
      toast.message("Claim released.");
      setMapOwnership?.((prev) => {
        const next = { ...prev }; delete next[regionId]; return next;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClaimBusy(false);
    }
  }

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="map-panel">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-amber-500 uppercase tracking-widest text-[10px] font-numeric">
            <Globe2 size={11} /> Chapter I · The Pillars
          </div>
          <h2 className="font-display text-3xl heading-gilded mt-0.5">The world of Territoria</h2>
          <p className="text-stone-500 font-body max-w-2xl">
            Every sovereign nation of Territoria. Every border, coalition and
            campaign is shaped by players — claim regions to fly your colours
            across the world.
          </p>
        </div>
        <div className="flex gap-4 text-[10px] font-numeric uppercase tracking-widest text-stone-500">
          <div><div className="text-stone-100 text-lg font-display leading-none">{nationCount ?? "…"}</div>nations</div>
          <div><div className="text-stone-100 text-lg font-display leading-none">{regionTotal ?? "…"}</div>regions</div>
          <div><div className="text-stone-100 text-lg font-display leading-none">{
            myHolderIso
              ? Object.values(mapOwnership || {}).filter((o) => o?.holder_iso === myHolderIso).length
              : 0
          }</div>held</div>
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4">
        <WorldMap
          selectedIso={selected?.iso_a3}
          onSelectCountry={(c) => { setSelected(c); setSelectedRegion(null); }}
          selectedRegionId={selectedRegion?.id}
          onSelectRegion={(r, c) => { setSelected(c); setSelectedRegion(r); }}
          onCountriesLoaded={(list) => {
            setNationCount(list.length);
            setRegionTotal(list.reduce((s, c) => s + (c.region_count || 0), 0));
            setCountriesReady(true);
            // Auto-focus the requested country on first paint:
            //   1. ?iso=XXX in the URL (used right after picking a homeland)
            //   2. the player's stored home_country_iso
            // We only run this once per mount so the user can navigate freely.
            if (!autoFocused) {
              const wanted = focusIso || homeIso;
              if (wanted) {
                const hit = list.find((c) => c.iso_a3 === wanted);
                if (hit) {
                  setSelected(hit);
                  setAutoFocused(true);
                }
              }
            }
          }}
          mapOwnership={mapOwnership}
          setMapOwnership={setMapOwnership}
          myHolderIso={myHolderIso}
          homeIso={homeIso}
          markers={presenceMarkers}
        />
        <CountryDetail
          entry={selected}
          selectedRegion={selectedRegion}
          onClose={() => { setSelected(null); setSelectedRegion(null); }}
          onSelectRegion={setSelectedRegion}
          mapOwnership={mapOwnership}
          myHolderIso={myHolderIso}
          homeIso={homeIso}
          onClaim={claim}
          onDeclareWar={declareWar}
          onAbandon={abandon}
          claimBusy={claimBusy}
        />
      </div>
    </div>
  );
}

function CountryDetail({
  entry, selectedRegion, onClose, onSelectRegion,
  mapOwnership = {}, myHolderIso, homeIso, onClaim, onDeclareWar, onAbandon,
  claimBusy,
}) {
  if (!entry) {
    return (
      <aside className="panel p-4 hidden xl:flex flex-col gap-3 text-sm text-stone-400 font-body">
        <div className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">
          Select a nation
        </div>
        <p className="text-stone-500 leading-relaxed">
          Click any coloured country on the map to open its region roster.
          Claim regions for gold to fly your colours across the world.
        </p>
        <div className="mt-auto text-[10px] font-numeric uppercase tracking-widest text-stone-600 border-t border-stone-800 pt-3">
          Phase 3 · player-driven ownership (live)<br/>
          Phase 4 · capital + campaign markers
        </div>
      </aside>
    );
  }
  const regions = entry.regions || [];
  const heldByMe = regions.filter((r) => mapOwnership[r.id]?.holder_iso === myHolderIso).length;
  const heldByOthers = regions.filter((r) => mapOwnership[r.id] && mapOwnership[r.id].holder_iso !== myHolderIso).length;
  // A region is peacefully claimable only by its sovereign country. Anyone
  // else (Germany looking at Poland) must Declare War.
  const isForeignSoil = !!myHolderIso && entry.iso_a3 !== myHolderIso;

  return (
    <aside className="panel p-4 flex flex-col gap-3" data-testid={`country-detail-${entry.iso_a3}`}>
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded shrink-0" style={{ background: entry.color, boxShadow: `0 0 24px ${entry.color}55` }} />
        <div className="flex-1 min-w-0">
          <div className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex items-center gap-1.5">
            {entry.faction} · {entry.iso_a3}
            {homeIso === entry.iso_a3 && (
              <span className="inline-flex items-center gap-1 text-amber-300" data-testid="homeland-badge">
                · <Home size={10} /> homeland
              </span>
            )}
          </div>
          <h3 className="font-display text-xl heading-gilded leading-tight truncate">{entry.name}</h3>
          {entry.legacy_name && (
            <div className="text-[11px] font-body italic text-stone-500 truncate">formerly {entry.legacy_name}</div>
          )}
        </div>
        <button onClick={onClose} className="text-stone-500 hover:text-stone-200" data-testid="country-detail-close">
          <X size={14} />
        </button>
      </div>

      <dl className="grid grid-cols-3 gap-2 text-[10px] font-numeric uppercase tracking-wider">
        <div className="rounded border border-stone-800 bg-[#0b0906] p-2">
          <dt className="text-stone-500">Regions</dt>
          <dd className="text-stone-100 mt-0.5 text-sm">{regions.length}</dd>
        </div>
        <div className="rounded border border-amber-800/40 bg-amber-900/10 p-2">
          <dt className="text-amber-500">Yours</dt>
          <dd className="text-stone-100 mt-0.5 text-sm">{heldByMe}</dd>
        </div>
        <div className="rounded border border-stone-800 bg-[#0b0906] p-2">
          <dt className="text-stone-500">Rivals</dt>
          <dd className="text-stone-100 mt-0.5 text-sm">{heldByOthers}</dd>
        </div>
      </dl>

      {regions.length > 0 && (
        <div className="flex flex-col gap-1.5 max-h-[46vh] overflow-y-auto pr-1 scroll-thin">
          <div className="text-[10px] font-numeric uppercase tracking-widest text-stone-500 sticky top-0 bg-[#0b0906]/95 backdrop-blur-sm py-1">
            Core regions
          </div>
          {regions.map((r) => {
            const isSel  = selectedRegion?.id === r.id;
            const owner  = mapOwnership[r.id];
            const mine   = owner && owner.holder_iso === myHolderIso;
            const swatch = owner?.faction_color || r.color;
            return (
              <div
                key={r.id}
                className={`rounded border p-2 transition-colors ${
                  isSel
                    ? "border-amber-600/70 bg-amber-900/10"
                    : mine
                      ? "border-amber-800/50 bg-amber-900/5"
                      : "border-stone-800 bg-[#0b0906]"
                }`}
                data-testid={`region-card-${r.id}`}
              >
                <button
                  className="w-full text-left"
                  onClick={() => onSelectRegion?.(isSel ? null : r)}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2.5 h-2.5 rounded-sm shrink-0 ${mine ? "ring-1 ring-amber-300" : ""}`}
                      style={{ background: swatch }}
                    />
                    <span className="text-sm text-stone-100 font-body flex-1 truncate">{r.name}</span>
                    <span className="text-[10px] font-numeric text-stone-500 shrink-0">wt {r.weight}</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="h-1 flex-1 bg-stone-800 rounded overflow-hidden">
                      <div
                        className="h-full rounded"
                        style={{ width: `${(r.weight / 5) * 100}%`, background: swatch, opacity: 0.85 }}
                      />
                    </div>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] font-numeric uppercase tracking-wider">
                    {owner ? (
                      <span className={mine ? "text-amber-400" : "text-stone-400"}>
                        <Flag size={9} className="inline mr-1" />
                        {mine ? "yours" : (owner.holder_name || owner.name)}
                      </span>
                    ) : (
                      <span className="text-stone-500 flex items-center gap-1">
                        <Coins size={9} /> {r.claim_cost}g
                      </span>
                    )}
                  </div>
                </button>
                <div className="mt-2 flex gap-1">
                  {mine ? (
                    <div
                      className="flex-1 text-[10px] font-numeric uppercase tracking-widest rounded border border-emerald-800/60 bg-emerald-950/30 text-emerald-300 py-1.5 text-center"
                      data-testid={`region-sovereign-${r.id}`}
                    >
                      Core Region · Held
                    </div>
                  ) : (
                    <button
                      disabled={claimBusy}
                      onClick={() => onDeclareWar?.(r.id)}
                      title="Only your country Leader may declare war"
                      className="flex-1 text-[11px] font-numeric uppercase tracking-wider rounded border border-red-800 bg-red-950/40 text-red-200 hover:bg-red-900/60 hover:border-red-500 py-1 disabled:opacity-50 transition-colors flex items-center justify-center gap-1"
                      data-testid={`region-declare-war-${r.id}`}
                    >
                      <Swords size={11} /> Declare War
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-auto text-[10px] font-body italic text-stone-600 border-t border-stone-800 pt-3">
        Ownership updates broadcast live over WebSocket — every player sees
        your colours the instant you claim a region.
      </div>
    </aside>
  );
}
